import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { mocApi } from '../api/client';
import { fmtDate } from '../lib/mocUtils';
import { Skeleton, EmptyBox } from './Validations';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import {
  ShieldCheck, ShieldWarning, FilePdf, CheckCircle, XCircle, Lock, UserPlus, Signature,
} from '@phosphor-icons/react';
import { PssrImportButton } from './PssrImport';

export default function Pssr() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  const [pssr, setPssr] = useState(null);

  useEffect(() => {
    if (id) mocApi.getPssr(id).then(setPssr);
    else mocApi.listPssr().then(setList);
  }, [id]);

  if (id) return <PssrDetail pssr={pssr} setPssr={setPssr} onBack={() => navigate('/pssr')} />;

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold">Pre-Startup Safety Review</div>
          <h1 className="font-display text-3xl font-black tracking-tight mt-1">PSSR</h1>
        </div>
        <PssrImportButton onImported={() => mocApi.listPssr().then(setList)} />
      </div>
      {!list ? <Skeleton /> : list.length === 0 ? <EmptyBox icon={ShieldCheck} text="Aucun PSSR." /> : (
        <div className="space-y-3">
          {list.map((p) => (
            <Card key={p.id} data-testid={`pssr-${p.id}`} className="rounded-sm p-4 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0">
                <ShieldWarning size={28} className={p.status === 'valide' ? 'text-emerald-500' : 'text-amber-500'} weight="bold" />
                <div className="min-w-0">
                  <div className="font-semibold text-sm truncate">{p.title}</div>
                  <div className="text-xs text-muted-foreground">{p.site} · MOC {p.mocId} · planifié {fmtDate(p.plannedAt)}</div>
                </div>
              </div>
              <Button size="sm" data-testid={`open-pssr-${p.id}`} onClick={() => navigate(`/pssr/${p.id}`)}>Ouvrir</Button>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function PssrDetail({ pssr, setPssr, onBack }) {
  if (!pssr) return <div className="h-64 bg-muted animate-pulse rounded-sm" />;
  if (!pssr.checklist) {
    return (
      <div className="space-y-4">
        <button data-testid="pssr-back" onClick={onBack} className="text-sm text-muted-foreground hover:text-foreground">← Retour</button>
        <Card className="rounded-sm p-6 text-center text-sm text-muted-foreground">
          La gestion du PSSR se fait désormais depuis l'onglet <b>PSSR</b> de la fiche MOC concernée.
        </Card>
      </div>
    );
  }

  const sign = async (name, decision) => {
    const res = await mocApi.signPssr(pssr.id, name, decision);
    if (res.ok) { setPssr({ ...res.pssr }); toast.success('Signature enregistrée'); }
  };

  const blockingOpen = pssr.checklist.some((c) => c.answer === 'non' && c.actionCreated);
  const themes = [...new Set(pssr.checklist.map((c) => c.theme))];

  return (
    <div className="space-y-5">
      <button data-testid="pssr-back" onClick={onBack} className="text-sm text-muted-foreground hover:text-foreground">← Retour aux PSSR</button>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-black tracking-tight">{pssr.title}</h1>
          <p className="text-sm text-muted-foreground mt-1">{pssr.site} · MOC {pssr.mocId} · planifié {fmtDate(pssr.plannedAt)}</p>
        </div>
        <Button variant="outline" size="sm" data-testid="pssr-pdf" onClick={() => toast.info('Génération du rapport PDF (côté serveur)')}>
          <FilePdf size={16} className="mr-1.5" />Rapport PDF
        </Button>
      </div>

      {/* Committee */}
      <Card className="rounded-sm p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-bold">Comité PSSR</h3>
          <Button size="sm" variant="ghost" data-testid="pssr-add-member" onClick={() => toast.info('Ajout de membre (côté serveur)')}><UserPlus size={16} className="mr-1.5" />Ajouter</Button>
        </div>
        <div className="space-y-2">
          {pssr.committee.map((m) => (
            <div key={m.name} data-testid={`committee-${m.name.replace(/\s/g, '-')}`} className="flex items-center justify-between border border-border rounded-sm p-3 gap-3 flex-wrap">
              <div><div className="text-sm font-medium">{m.name}</div><div className="text-xs text-muted-foreground">{m.role}</div></div>
              {m.signed ? (
                <span className={`text-xs font-semibold inline-flex items-center gap-1 ${m.decision === 'oui' ? 'text-emerald-600' : 'text-red-600'}`}>
                  <Signature size={14} weight="bold" />Signé — {m.decision === 'oui' ? 'Favorable' : 'Défavorable'}
                </span>
              ) : (
                <div className="flex gap-2">
                  <Button size="sm" data-testid={`sign-yes-${m.name.replace(/\s/g, '-')}`} onClick={() => sign(m.name, 'oui')}><CheckCircle size={14} className="mr-1" />Oui</Button>
                  <Button size="sm" variant="outline" data-testid={`sign-no-${m.name.replace(/\s/g, '-')}`} onClick={() => sign(m.name, 'non')}><XCircle size={14} className="mr-1" />Non</Button>
                </div>
              )}
            </div>
          ))}
        </div>
      </Card>

      {/* Checklist by theme */}
      <Card className="rounded-sm p-4">
        <h3 className="font-display font-bold mb-1">Checklist de sécurité</h3>
        <p className="text-xs text-muted-foreground mb-4">Toute réponse « Non » génère automatiquement une action (créée côté serveur).</p>
        {themes.map((t) => (
          <div key={t} className="mb-4">
            <div className="text-xs uppercase tracking-wide text-muted-foreground font-semibold mb-2">{t}</div>
            <div className="space-y-2">
              {pssr.checklist.filter((c) => c.theme === t).map((c) => (
                <div key={c.id} className="flex items-start justify-between gap-3 border border-border rounded-sm p-3">
                  <span className="text-sm">{c.question}</span>
                  <div className="shrink-0 text-right">
                    {c.answer === 'oui' && <span className="text-xs font-semibold text-emerald-600">Oui</span>}
                    {c.answer === 'non' && <span className="text-xs font-semibold text-red-600">Non → {c.actionCreated}</span>}
                    {!c.answer && <span className="text-xs text-muted-foreground">Non renseigné</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </Card>

      {/* Authorization */}
      <Card className="rounded-sm p-4">
        <h3 className="font-display font-bold mb-1">Autorisation de mise en route</h3>
        {blockingOpen ? (
          <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 dark:bg-red-900/30 dark:text-red-300 border border-red-200 dark:border-red-800 rounded-sm p-3 mt-2">
            <Lock size={18} className="shrink-0 mt-0.5" />
            L'autorisation est <strong>bloquée</strong> : au moins une action bloquante issue de la checklist est ouverte. La double signature sera débloquée par le serveur une fois les actions traitées.
          </div>
        ) : (
          <p className="text-sm text-muted-foreground mt-2">Les 3 décisions et la double signature électronique sont disponibles.</p>
        )}
        <div className="grid sm:grid-cols-3 gap-3 mt-4">
          {['Mise en route autorisée', 'Autorisée avec conditions', 'Refusée'].map((d, i) => (
            <label key={i} data-testid={`auth-decision-${i}`} className={`border rounded-sm p-3 text-sm ${blockingOpen ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-muted/50'}`}>
              <RadioGroup disabled={blockingOpen}><div className="flex items-center gap-2"><RadioGroupItem value={String(i)} disabled={blockingOpen} />{d}</div></RadioGroup>
            </label>
          ))}
        </div>
        <div className="flex gap-3 mt-4">
          <Button disabled={blockingOpen} data-testid="auth-sign-1"><Signature size={16} className="mr-1.5" />Signature responsable</Button>
          <Button variant="outline" disabled={blockingOpen} data-testid="auth-sign-2"><Signature size={16} className="mr-1.5" />Signature HSE</Button>
        </div>
      </Card>
    </div>
  );
}
